import React from "react";
import { Lock, LogOut, CreditCard } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function formatExpiry(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return null;
  }
}

export default function TrialExpired() {
  const { user, logout } = useAuth();
  const trial = user?.trial;
  const expiredOn = formatExpiry(trial?.expires_at);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2A2623] flex items-center justify-center px-4" data-testid="trial-expired-page">
      <div className="w-full max-w-lg rounded-2xl border border-[#E8E1D6] bg-[#FFFFFF] p-8 sm:p-10">
        <div className="w-14 h-14 rounded-full bg-[#0F6B6B]/10 border border-[#0F6B6B]/40 flex items-center justify-center">
          <Lock className="w-7 h-7 text-[#0F6B6B]" strokeWidth={2.4} />
        </div>
        <h1 className="mt-6 text-3xl sm:text-4xl font-black tracking-tight">Your 15-day free trial has ended</h1>
        {expiredOn && (
          <p className="mt-2 text-sm text-[#6B635C]" data-testid="trial-expired-date">
            Trial ended on <span className="text-[#2A2623] font-semibold">{expiredOn}</span>
          </p>
        )}
        <p className="mt-6 text-[#2A2623] leading-relaxed">
          To keep your clients, sessions, invoices and backups, subscribe to the flat plan of
          <span className="text-[#2A2623] font-semibold"> $29 AUD/month</span>. No tiers. No hidden fees. No percentage of your client payments.
        </p>

        <div className="mt-8 rounded-xl border border-[#E8E1D6] bg-[#FAF7F2] p-5 space-y-3 text-sm text-[#2A2623]">
          <div className="flex items-start gap-3">
            <CreditCard className="w-4 h-4 text-[#0F6B6B] mt-0.5 shrink-0" />
            <div>
              <div className="text-[#2A2623] font-semibold">Online payments coming soon</div>
              <div className="text-[#6B635C] mt-1">
                Card checkout isn&rsquo;t live yet. Email <a href="mailto:hello@trainmate.au" className="text-[#0F6B6B] underline underline-offset-2">hello@trainmate.au</a> from your account address and we&rsquo;ll extend your access while you finish onboarding.
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <a
            href="mailto:hello@trainmate.au?subject=TrainMate%20subscription&body=Hi%20TrainMate%2C%20I%27d%20like%20to%20subscribe%20and%20keep%20my%20account%20active."
            data-testid="trial-expired-contact"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-[#0F6B6B] text-[#FFFFFF] font-bold uppercase tracking-wider text-sm px-5 py-3"
          >
            <CreditCard className="w-4 h-4" strokeWidth={2.8} />
            Contact to subscribe
          </a>
          <button
            type="button"
            onClick={logout}
            data-testid="trial-expired-logout"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-[#E8E1D6] text-[#2A2623] hover:text-[#2A2623] hover:border-[#C9BFAF] px-5 py-3 text-sm font-semibold"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>

        <p className="mt-6 text-xs text-[#6B635C]">
          Signed in as <span className="text-[#2A2623]">{user?.email}</span>. Your data is safe. Nothing is deleted while your trial is expired.
        </p>
      </div>
    </div>
  );
}
